import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, NavigationStart, UrlTree, Params, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter, take } from 'rxjs/operators';
import { environment } from '@enviroment/environment';

/**
 * @description Servicio global para la persistencia automática de query parameters durante la navegación.
 * Se activa cuando el ambiente es 'test' o existe cualquier parámetro UTM en la URL actual.
 * Persiste parámetros específicos con valores esperados y parámetros UTM/marketing con cualquier valor.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalQueryParamsPreserverService {
  private environment = environment;

  private readonly PARAMS_TO_PERSIST_CONFIG = [{ key: 'rtir', value: 'd3f2220e-4acc-4156-935d-bfcf3f80adf2' }];

  private readonly UTM_PARAMS_TO_PERSIST = [
    'utm_source',
    'utm_medium',
    'director',
    'campana',
    'accion',
    'medio',
    'anuncios',
    'detalle',
    'ref',
    'asesor'
  ];

  private activePersistentParams: Params = {};
  private shouldPersistUtmParams: boolean = true;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // Inicializar inmediatamente si el ambiente es 'test'
      if (this.environment.ENVIRONMENT_NAME === 'test') {
        this.initGlobalQueryParamsPreserver();
        return;
      }

      // Para otros ambientes, esperar a que la navegación termine para verificar UTM params
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1),
        )
        .subscribe(() => {
          const hasUtmParams = this.hasUtmParamsInUrl();
          
          if (hasUtmParams) {
            this.initGlobalQueryParamsPreserver();
          }
        });
    }
  }

  /**
   * @description Verifica si existen parámetros UTM en la URL actual
   */
  private hasUtmParamsInUrl(): boolean {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const hasParams = this.UTM_PARAMS_TO_PERSIST.some((param) => urlTree.queryParams[param] !== undefined);
    
    return hasParams;
  }

  /**
   * @description Desactiva la persistencia de parámetros UTM y los remueve de la URL
   * Debe ser llamado después del procesamiento de HubSpot
   */
  public stopPersistingAndRemoveUtmParams(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.shouldPersistUtmParams = false;
    this.clearUtmParamsFromMemory();
    this.removeUtmParamsFromCurrentUrl();
  }

  /**
   * @description Limpia los parámetros UTM de la memoria persistente
   */
  private clearUtmParamsFromMemory(): void {
    this.UTM_PARAMS_TO_PERSIST.forEach(param => {
      if (this.activePersistentParams[param] !== undefined) {
        delete this.activePersistentParams[param];
      }
    });
  }

  /**
   * @description Remueve los parámetros UTM de la URL actual si existen
   */
  private removeUtmParamsFromCurrentUrl(): void {
    const { filteredParams, hasUtmParams } = this.filterUtmParamsFromCurrentUrl();

    if (hasUtmParams) {
      const currentUrl = this.router.url;
      const urlTree = this.router.parseUrl(currentUrl);
      urlTree.queryParams = filteredParams;
      this.router.navigateByUrl(urlTree, { replaceUrl: true });
    }
  }

  /**
   * @description Filtra los parámetros UTM de la URL actual
   */
  private filterUtmParamsFromCurrentUrl(): { filteredParams: Params, hasUtmParams: boolean } {
    const currentUrl = this.router.url;
    const urlTree = this.router.parseUrl(currentUrl);
    const currentParams = { ...urlTree.queryParams };
    const filteredParams: { [key: string]: any } = {};
    let hasUtmParams = false;

    Object.keys(currentParams).forEach(key => {
      if (!this.UTM_PARAMS_TO_PERSIST.includes(key)) {
        filteredParams[key] = currentParams[key];
      } else {
        hasUtmParams = true;
      }
    });

    return { filteredParams, hasUtmParams };
  }

  /**
   * @description Inicializa el preservador global de query parameters
   */
  private initGlobalQueryParamsPreserver(): void {
    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        const targetUrlTree: UrlTree = this.router.parseUrl(event.url);
        const currentUrlTree: UrlTree = this.router.parseUrl(this.router.url);
        let shouldUpdateUrl = false;

        const isNavigatingToMisProcesos = this.isNavigatingToSpecialRoute(targetUrlTree, 'mis-procesos');

        shouldUpdateUrl = this.handleSpecificParams(targetUrlTree, currentUrlTree) || shouldUpdateUrl;
        shouldUpdateUrl = this.handleUtmParams(targetUrlTree, currentUrlTree, isNavigatingToMisProcesos) || shouldUpdateUrl;
        
        // Si tenemos parámetros persistentes y la URL de destino no los tiene, necesitamos actualizarla
        if (!shouldUpdateUrl && Object.keys(this.activePersistentParams).length > 0) {
          // Verificar si algún parámetro persistente no está en la URL de destino
          const needsUpdate = Object.keys(this.activePersistentParams).some(key => {
            const paramValue = this.activePersistentParams[key];
            const targetHasParam = targetUrlTree.queryParams[key] !== undefined;
            const isUtmParam = this.UTM_PARAMS_TO_PERSIST.includes(key);
            
            // Si es un param UTM y no debemos persistir UTMs, no actualizar
            if (isUtmParam && (!this.shouldPersistUtmParams || isNavigatingToMisProcesos)) {
              return false;
            }
            
            return !targetHasParam;
          });
          
          if (needsUpdate) {
            shouldUpdateUrl = true;
          }
        }

        if (shouldUpdateUrl) {
          const finalQueryParams = this.buildFinalQueryParams(targetUrlTree, isNavigatingToMisProcesos);
          this.navigateWithUpdatedParams(event.url, finalQueryParams);
        }
      });
  }

  /**
   * @description Verifica si estamos navegando a una ruta especial
   */
  private isNavigatingToSpecialRoute(targetUrlTree: UrlTree, routeName: string): boolean {
    return targetUrlTree.root.children['primary']?.segments[0]?.path === routeName;
  }

  /**
   * @description Maneja parámetros con valores específicos esperados (como rtir)
   */
  private handleSpecificParams(targetUrlTree: UrlTree, currentUrlTree: UrlTree): boolean {
    let shouldUpdateUrl = false;

    this.PARAMS_TO_PERSIST_CONFIG.forEach((paramConfig) => {
      const { key, value: expectedValue } = paramConfig;

      if (targetUrlTree.queryParams[key] === expectedValue) {
        if (this.activePersistentParams[key] !== expectedValue) {
          this.activePersistentParams[key] = expectedValue;
          shouldUpdateUrl = true;
        }
      } else if (currentUrlTree.queryParams[key] === expectedValue) {
        if (this.activePersistentParams[key] !== expectedValue) {
          this.activePersistentParams[key] = expectedValue;
          shouldUpdateUrl = true;
        }
      } else if (this.activePersistentParams[key] !== undefined) {
        delete this.activePersistentParams[key];
        shouldUpdateUrl = true;
      }
    });

    return shouldUpdateUrl;
  }

  /**
   * @description Maneja parámetros UTM basado en las condiciones de persistencia
   */
  private handleUtmParams(targetUrlTree: UrlTree, currentUrlTree: UrlTree, isNavigatingToMisProcesos: boolean): boolean {
    if (isNavigatingToMisProcesos || !this.shouldPersistUtmParams) {
      return false;
    }

    let shouldUpdateUrl = false;

    this.UTM_PARAMS_TO_PERSIST.forEach((utmParam) => {
      if (targetUrlTree.queryParams[utmParam] !== undefined) {
        const value = targetUrlTree.queryParams[utmParam];
        if (this.activePersistentParams[utmParam] !== value) {
          this.activePersistentParams[utmParam] = value;
          shouldUpdateUrl = true;
        }
      } else if (currentUrlTree.queryParams[utmParam] !== undefined) {
        const value = currentUrlTree.queryParams[utmParam];
        if (this.activePersistentParams[utmParam] !== value) {
          this.activePersistentParams[utmParam] = value;
          shouldUpdateUrl = true;
        }
      }
    });

    return shouldUpdateUrl;
  }

  /**
   * @description Construye los query parameters finales para la navegación
   */
  private buildFinalQueryParams(targetUrlTree: UrlTree, isNavigatingToMisProcesos: boolean): Params {
    const finalQueryParams: Params = { ...targetUrlTree.queryParams };

    if (isNavigatingToMisProcesos || !this.shouldPersistUtmParams) {
      this.removeUtmParamsFromObject(finalQueryParams);
    }

    this.addActivePersistentParams(finalQueryParams);
    this.ensureRequiredParamsPresent(finalQueryParams, isNavigatingToMisProcesos);

    return finalQueryParams;
  }

  /**
   * @description Remueve parámetros UTM de un objeto de parámetros
   */
  private removeUtmParamsFromObject(params: Params): void {
    this.UTM_PARAMS_TO_PERSIST.forEach((utmParam) => {
      if (params[utmParam] !== undefined) {
        delete params[utmParam];
      }
    });
  }

  /**
   * @description Agrega parámetros persistentes activos a los parámetros finales
   */
  private addActivePersistentParams(finalQueryParams: Params): void {
    for (const key in this.activePersistentParams) {
      if (Object.prototype.hasOwnProperty.call(this.activePersistentParams, key)) {
        const value = this.activePersistentParams[key];
        
        if (this.UTM_PARAMS_TO_PERSIST.includes(key) && !this.shouldPersistUtmParams) {
          continue;
        }
        
        if (finalQueryParams[key] !== value) {
          finalQueryParams[key] = value;
        }
      }
    }
  }

  /**
   * @description Asegura que los parámetros requeridos estén presentes
   */
  private ensureRequiredParamsPresent(finalQueryParams: Params, isNavigatingToMisProcesos: boolean): void {
    this.PARAMS_TO_PERSIST_CONFIG.forEach((paramConfig) => {
      const key = paramConfig.key;
      if (this.activePersistentParams[key] && finalQueryParams[key] === undefined) {
        finalQueryParams[key] = this.activePersistentParams[key];
      }
    });

    if (!isNavigatingToMisProcesos && this.shouldPersistUtmParams) {
      this.UTM_PARAMS_TO_PERSIST.forEach((utmParam) => {
        if (this.activePersistentParams[utmParam] && finalQueryParams[utmParam] === undefined) {
          finalQueryParams[utmParam] = this.activePersistentParams[utmParam];
        }
      });
    }
  }

  /**
   * @description Navega con los parámetros actualizados
   */
  private navigateWithUpdatedParams(originalUrl: string, finalQueryParams: Params): void {
    const urlToNavigate = this.router.parseUrl(originalUrl);
    urlToNavigate.queryParams = finalQueryParams;
    this.router.navigateByUrl(urlToNavigate, { replaceUrl: true });
  }
}
